import * as L from 'leaflet';
/**
 * The map management.
 * @extends external:L.Evented
 */
export class ManagementMap extends L.Evented {
    /**
     * Create a new map management instance.
     * @param widget - The M.A.A.P. instance.
     * @param options - The map management options.
     */
    constructor(widget, options) {
        super();
        /**
         * The map management options.
         */
        this.options = {
            center: [46.801111, 8.226667],
            zoom: 12,
            maxZoom: 18,
            minZoom: 0,
            zoomDelta: 1,
            zoomSnap: 1,
            wheelPxPerZoomLevel: 60,
            zoomControl: true,
            maxBounds: null,
            fadeAnimation: false,
            attributionControl: false,
            gestureHandling: true,
            shouldLogZoomLevel: false,
        };
        this.widget = widget;
        const gestureHandlingOptions = {
            text: {
                touch: this.widget.i18n.translate('gestureHandlingTouch'),
                scroll: this.widget.i18n.translate('gestureHandlingScroll'),
                scrollMac: this.widget.i18n.translate('gestureHandlingScrollMac'),
            },
            // As the default gesture handling options get overwritten, we have to
            // specify a duration here. 1 second is also the plugin's default value.
            duration: 1 * 1000,
        };
        this.options = Object.assign(Object.assign(Object.assign({}, this.options), options), { gestureHandlingOptions });
        this.options.center = L.latLng(this.options.center);
        this.addEventParent(widget);
        this._container = L.DomUtil.create('div', 'maap_p-maap__map', widget._widgetContainer);
        this._map = L.map(this._container, this.getLeafletMapOptions());
        this._map.addEventParent(this);
        if (this.options.shouldLogZoomLevel) {
            this.enableZoomLevelLogging();
        }
    }
    /**
     * Add a layer.
     * @param layer - The layer to add.
     */
    addLayer(layer) {
        this._map.addLayer(layer);
    }
    /**
     * Remove a layer.
     * @param layer - The layer to remove.
     */
    removeLayer(layer) {
        this._map.removeLayer(layer);
    }
    /**
     * Destroys the map and removes all related event listeners and containers.
     */
    destroy() {
        this.removeEventParent(this.widget);
        this._map.remove();
        L.DomUtil.remove(this._container);
    }
    /**
     * Get the leaflet map options.
     * @returns The leaflet map options.
     */
    getLeafletMapOptions() {
        var _a;
        return {
            center: this.options.center,
            zoom: this.options.zoom,
            maxZoom: this.options.maxZoom,
            minZoom: this.options.minZoom,
            zoomDelta: this.options.zoomDelta,
            zoomSnap: this.options.zoomSnap,
            wheelPxPerZoomLevel: this.options.wheelPxPerZoomLevel,
            zoomControl: this.options.zoomControl,
            maxBounds: (_a = this.options.maxBounds) !== null && _a !== void 0 ? _a : undefined,
            fadeAnimation: this.options.fadeAnimation,
            attributionControl: this.options.attributionControl,
            gestureHandling: this.options.gestureHandling,
            gestureHandlingOptions: this.options.gestureHandlingOptions,
        };
    }
    /**
     * Enable the logging of the zoom level at the end of the zoom event to the
     * browser console.
     */
    enableZoomLevelLogging() {
        this._map.on('zoomend', () => {
            const leafletZoomLevel = this._map.getZoom();
            const zoomLevels = [
                { label: 'Leaflet', zoomLevel: leafletZoomLevel },
                // https://docs.mapbox.com/help/glossary/zoom-level/#tile-size
                { label: 'Mapbox GL', zoomLevel: leafletZoomLevel - 1 },
            ];
            console.group('Zoomed to level:');
            zoomLevels.forEach(({ label, zoomLevel }) => {
                console.log(`%c${label}: %c${zoomLevel}`, 'font-style:italic', 'font-style:normal');
            });
            console.groupEnd();
        });
    }
}
//# sourceMappingURL=ManagementMap.js.map